/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { ReactNode, useContext, useEffect, useState } from 'react';

import CvesContext from '@totem/components/software/cves/cvesContext';
import { CVERecord } from '@totem/components/software/cves/types';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { isNotNull } from '@totem/utilities/common';
import { DEVICES_ENDPOINT } from '@totem/utilities/endpoints';

type Props = {
  ids: string[];
  version?: string;
  children?: ReactNode;
};

const CvesContainer = ({ ids, version, children }: Props) => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const [prevIds, setPrevIds] = useState<string[]>(ids);
  const [refreshData, setRefreshData] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [data, setData] = useState<CVERecord[]>(null);
  const [rawData, setRawData] = useState<CVERecord[]>(null);
  const [selectedItem, setSelectedItem] = useState<CVERecord>(null);
  const [action, setAction] = useState<string>('');

  useEffect(() => {
    setRefreshData(true);
  }, [userProfile.organization.id]);

  useEffect(() => {
    if (refreshData || ids.length !== prevIds.length) {
      setRefreshData(false);

      setPrevIds(ids);

      setIsLoading(true);

      const uri = `${DEVICES_ENDPOINT}/software/cves`;

      fetch(uri, {
        method: 'POST',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
        body: JSON.stringify(ids.filter((chk) => chk !== '')),
      })
        .then((res) => (res.ok ? res.json() : []))
        .then((result: CVERecord[]) => {
          const cves = Array.isArray(result) ? result : [];

          setRawData(cves);
          if (isNotNull(version)) {
            setData(
              cves.filter(
                (chk) =>
                  chk.metaData.affectedVersions.findIndex(
                    (chk2) => chk2 === version,
                  ) >= 0,
              ),
            );
          } else {
            setData(cves);
          }
        })
        .then(() => {
          setIsLoading(false);
        })
        .catch(() => {
          setRawData([]);

          setData([]);

          setIsLoading(false);
        });
    }
  }, [ids, refreshData, getApiToken]);

  const handleAction = (newAction: string, cve: CVERecord) => {
    switch (newAction) {
      case 'refresh':
        setRefreshData(true);

        setAction('');

        setSelectedItem(null);
        break;
      default:
        setAction(newAction);

        setSelectedItem(cve);
    }
  };

  return (
    <CvesContext.Provider
      value={{
        loading: isLoading,
        ids,
        data,
        selectedItem,
        action,
        onAction: handleAction,
      }}
    >
      {children}
    </CvesContext.Provider>
  );
};

export default CvesContainer;
